import { Button } from '@/components/shadcn/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/shadcn/dialog'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/shadcn/form'
import { Input } from '@/components/shadcn/input'
import { SingleSelectComboboxField } from '@/components/form-components/SingleSelectCombobox'
import { RestSchemas, useRestClient } from '@/contexts/QueryProvider'
import { useSessionStorage } from '@/hooks'
import { isSuperUser } from '@/util'
import { zodResolver } from '@hookform/resolvers/zod'
import { IconDeleteOutline } from '@icons/IconDeleteOutline'
import { useCallback, useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { useParams } from 'react-router'
import { toast } from 'sonner'
import { z } from 'zod'
import { SkillSummary } from '../PlatformAgentSkillsTable/platformAgentSkillsTableUtils'

type SkillMemberSummary = RestSchemas['Messages.Skill.SkillMemberSummary']
type ProjectUser = RestSchemas['Messages.Projects.ProjectUser']
type PlatformUser = RestSchemas['Messages.Platform.PlatformUserWithAudit']

const addMemberSchema = z.object({
  userId: z.coerce.number().int().positive('Enter a valid user ID'),
})

type AddMemberForm = z.infer<typeof addMemberSchema>

type SkillMembersDialogProps = {
  skill: SkillSummary
  open: boolean
  onOpenChange: (open: boolean) => void
}

function getUserLabel(user: ProjectUser | PlatformUser) {
  if ('email' in user && user.email) {
    return user.email
  }

  return `User ${user.id}`
}

export function SkillMembersDialog({ skill, open, onOpenChange }: SkillMembersDialogProps) {
  const params = useParams()
  const projectId = params.projectId ? Number(params.projectId) : undefined
  const [roles] = useSessionStorage('roles', 0)
  const canBrowsePlatformUsers = isSuperUser(roles)

  const {
    getSkillApiGetSkillMembers,
    putSkillApiAddSkillMember,
    deleteSkillApiRemoveSkillMember,
    getSupportApiGetPlatformUsers,
    getProjectsApiGetUsers,
  } = useRestClient()

  const skillId = skill.id ?? 0

  const { data: members, isLoading: membersLoading } = getSkillApiGetSkillMembers.useQuery(
    { path: { skillId } },
    { enabled: open && skillId > 0 }
  )

  const { data: projectUsers } = getProjectsApiGetUsers.useQuery(
    { query: { projectId: projectId! } },
    { enabled: open && !!projectId }
  )

  const { data: platformUsers } = getSupportApiGetPlatformUsers.useQuery(undefined, {
    enabled: open && !projectId && canBrowsePlatformUsers,
  })

  const invalidateMembers = () => {
    getSkillApiGetSkillMembers.invalidateQueries({ parameters: { path: { skillId } } })
  }

  const { mutate: addMember, isPending: isAddingMember } = putSkillApiAddSkillMember.useMutation(undefined, {
    onSuccess: () => {
      invalidateMembers()
      toast.success('Member added')
    },
    onError: () => {
      toast.error('Error adding member')
    },
  })

  const { mutate: removeMember, isPending: isRemovingMember } = deleteSkillApiRemoveSkillMember.useMutation(undefined, {
    onSuccess: () => {
      invalidateMembers()
      toast.success('Member removed')
    },
    onError: () => {
      toast.error('Error removing member')
    },
  })

  const form = useForm<AddMemberForm>({
    resolver: zodResolver(addMemberSchema),
    defaultValues: { userId: 0 },
  })

  useEffect(() => {
    if (!open) {
      form.reset({ userId: 0 })
    }
  }, [form, open])

  const memberUserIds = useMemo(
    () => new Set((members ?? []).map((member) => member.userId).filter((id): id is number => !!id)),
    [members]
  )

  const userOptions = useMemo(() => {
    const selectableUsers = projectUsers ?? platformUsers ?? []

    return selectableUsers
      .filter((user) => user.id && user.id !== skill.ownerId && !memberUserIds.has(user.id))
      .map((user) => ({
        value: user.id,
        label: getUserLabel(user),
        description: `ID ${user.id}`,
      }))
  }, [memberUserIds, platformUsers, projectUsers, skill.ownerId])

  const useUserPicker = userOptions.length > 0

  const handleAddMember = useCallback(
    (data: AddMemberForm) => {
      addMember({
        path: { skillId },
        body: { userId: data.userId },
      })
      form.reset({ userId: 0 })
    },
    [addMember, form, skillId]
  )

  const handleRemoveMember = (member: SkillMemberSummary) => {
    if (!member.userId) return
    removeMember({
      path: {
        skillId,
        userId: member.userId,
      },
    })
  }

  const handleInvalidSubmit = useCallback(() => {
    toast.error('Enter a valid user before adding a member')
  }, [])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[640px]">
        <DialogHeader>
          <DialogTitle>Manage members</DialogTitle>
          <DialogDescription>
            Share &quot;{skill.name}&quot; with additional users. The owner always has access.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(handleAddMember, handleInvalidSubmit)} className="space-y-4">
              {useUserPicker ? (
                <SingleSelectComboboxField
                  control={form.control}
                  name="userId"
                  label="Add member"
                  options={userOptions}
                  placeholder="Select a user..."
                  searchPlaceholder="Search users..."
                  emptyMessage="No eligible users found."
                  allowClear={false}
                />
              ) : (
                <FormField
                  control={form.control}
                  name="userId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>User ID</FormLabel>
                      <FormControl>
                        <Input type="number" min={1} step={1} {...field} />
                      </FormControl>
                      <FormMessage />
                      <p className="text-sm text-muted-foreground">
                        Enter the DMP user ID for the person you want to share this skill with.
                      </p>
                    </FormItem>
                  )}
                />
              )}
              <DialogFooter className="px-0">
                <Button type="submit" disabled={isAddingMember}>
                  Add member
                </Button>
              </DialogFooter>
            </form>
          </Form>

          <div className="space-y-3">
            <h4 className="text-sm font-medium">Current members</h4>
            {membersLoading ? (
              <p className="text-sm text-muted-foreground">Loading members...</p>
            ) : (members ?? []).length === 0 ? (
              <p className="text-sm text-muted-foreground">No additional members yet.</p>
            ) : (
              <ul className="divide-y rounded-md border">
                {(members ?? []).map((member) => (
                  <li key={member.id ?? member.userId} className="flex items-center justify-between gap-3 px-4 py-3">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium">{member.userName || `User ${member.userId}`}</p>
                      <p className="text-xs text-muted-foreground">User ID {member.userId}</p>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      disabled={isRemovingMember}
                      onClick={() => handleRemoveMember(member)}
                    >
                      <IconDeleteOutline size={16} />
                      <span className="sr-only">Remove member</span>
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
